import { altLabel, modLabel } from "../lib/platform";

export interface ShortcutEntry {
  /** Key tokens joined by `+`; alternatives separated by ` or `. */
  keys: string;
  action: string;
  /** True once the shell actually implements the binding. */
  implemented?: boolean;
}

export interface ShortcutGroup {
  name: string;
  entries: ShortcutEntry[];
}

/**
 * The documented shortcut set (note 03 §10), kept as data so the help dialog,
 * the command palette, and the key handler all read from one table. Entries
 * whose feature has not landed yet are still listed — the dialog is a map of the
 * product, and later waves flip `implemented` on as they wire each one up.
 */
export function shortcutCatalog(): ShortcutGroup[] {
  const mod = modLabel();
  const alt = altLabel();

  return [
    {
      name: "Navigation",
      entries: [
        { keys: `${mod}+K`, action: "Open command menu", implemented: true },
        { keys: `${mod}+[ or ${mod}+]`, action: "Back or forward" },
        { keys: "N", action: "New document", implemented: true },
        { keys: "E", action: "Edit current document", implemented: true },
        { keys: "M", action: "Move current document", implemented: true },
        { keys: "H", action: "Open document history", implemented: true },
        { keys: "/ or T", action: "Jump to search", implemented: true },
        { keys: "D", action: "Jump to home", implemented: true },
        { keys: "Ctrl+Alt+H", action: "Table of contents", implemented: true },
        { keys: `${mod}+.`, action: "Toggle sidebar", implemented: true },
        { keys: `${mod}+Shift+L`, action: "Toggle theme", implemented: true },
        { keys: `${mod}+Shift+G`, action: "Toggle statistics" },
        { keys: `${mod}+F`, action: "Focus search input" },
        { keys: "?", action: "Open shortcuts guide", implemented: true },
        { keys: `${mod}+Enter`, action: "Follow link under cursor" },
        { keys: `${mod}+${alt}+P`, action: "Present document" },
        { keys: `${mod}+Shift+P`, action: "Publish and exit" },
        { keys: `${mod}+Esc or Shift+Esc`, action: "Cancel editing" },
        { keys: `${mod}+Shift+I`, action: "Open insights" },
      ],
    },
    {
      name: "Collaboration",
      entries: [{ keys: `${mod}+${alt}+M`, action: "Comment on selection" }],
    },
    {
      name: "Formatting",
      entries: [
        { keys: "Ctrl+Shift+0…3", action: "Paragraph, large, medium, small header" },
        { keys: "Ctrl+Shift+\\", action: "Code block" },
        { keys: `${mod}+B`, action: "Bold" },
        { keys: `${mod}+I`, action: "Italic" },
        { keys: `${mod}+U`, action: "Underline" },
        { keys: `${mod}+D`, action: "Strikethrough" },
        { keys: `${mod}+Shift+C`, action: "Inline code" },
        { keys: `${mod}+Shift+H`, action: "Highlight" },
        { keys: `${mod}+K`, action: "Link (with a selection)" },
        { keys: `${mod}+Z or ${mod}+Shift+Z`, action: "Undo or redo" },
        { keys: `${mod}+${alt}+↑ or ${mod}+${alt}+↓`, action: "Move block up or down" },
      ],
    },
    {
      name: "Lists",
      entries: [
        { keys: "Ctrl+Shift+7/8/9", action: "Ordered, bulleted, todo list" },
        { keys: `${mod}+Enter`, action: "Split or exit the list item" },
        { keys: "Tab or Shift+Tab", action: "Indent or outdent" },
        { keys: `${alt}+↑ or ${alt}+↓`, action: "Move item" },
      ],
    },
    {
      name: "Toggle blocks and tables",
      entries: [
        { keys: `${mod}+Enter`, action: "Insert" },
        { keys: "Tab or Shift+Tab", action: "Next or previous cell" },
      ],
    },
    {
      name: "Markdown",
      entries: [
        { keys: "#␣, ##␣, ###␣", action: "Headings" },
        { keys: "1.␣ or -␣", action: "Ordered or bulleted list" },
        { keys: ">␣", action: "Blockquote" },
        { keys: "---", action: "Horizontal rule" },
        { keys: "|--", action: "Table" },
        { keys: "```␣", action: "Code block" },
        { keys: "$$$␣", action: "Math block" },
        { keys: "+++␣", action: "Toggle block" },
        { keys: ":::", action: "Notice block" },
      ],
    },
    {
      name: "Triggers",
      entries: [
        { keys: "@", action: "Mention a document or user" },
        { keys: "+", action: "Mention a user" },
        { keys: "/", action: "Block menu" },
        { keys: ":", action: "Emoji" },
      ],
    },
  ];
}

/** Split a `keys` string into renderable chunks: key tokens and the word "or". */
export function splitKeys(keys: string): string[] {
  return keys.split(/(\bor\b)/u).flatMap((part) => {
    const trimmed = part.trim();
    if (trimmed === "") return [];
    if (trimmed === "or") return ["or"];
    return trimmed.split("+").map((token) => token.trim());
  });
}
